import React, { createContext, useContext, useMemo } from 'react';

import { catalogs } from './messages';
import type { Messages } from './messages';
import { DEFAULT_LOCALE, localizePath, type Locale } from './locales';

type I18nContextValue = {
  locale: Locale;
  m: Messages;
  localizePath: (pathname: string) => string;
  pathForLocale: (nextLocale: Locale) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

type I18nProviderProps = {
  locale: Locale;
  pathname: string;
  children: React.ReactNode;
};

export function I18nProvider({ locale, pathname, children }: I18nProviderProps) {
  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      m: catalogs[locale],
      localizePath: (path: string) => localizePath(path, locale),
      pathForLocale: (nextLocale: Locale) => localizePath(pathname, nextLocale),
    }),
    [locale, pathname],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);

  if (!context) {
    return {
      locale: DEFAULT_LOCALE,
      m: catalogs[DEFAULT_LOCALE],
      localizePath: (path: string) => localizePath(path, DEFAULT_LOCALE),
      pathForLocale: (nextLocale: Locale) => localizePath('/', nextLocale),
    };
  }

  return context;
}
